/**
 * <noia-product-gallery> — the product photo column.
 *
 * - Mobile: one photo per swipe; tapping a photo changes it (left third back, else forward); [data-gallery-counter] shows (n/total).
 * - Desktop: clicking a photo ([data-gallery-item]) opens the lightbox <dialog data-lightbox> with that photo;
 *   (CLOSE), previous/next buttons and the arrow keys navigate. Escape closes (native dialog).
 * - When a variant option changes inside the product's <s-variant-picker>, the variant's photo
 *   (from the [data-variant-media] JSON map, variantId → mediaId) moves to the top of the column.
 */
class NoiaProductGallery extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector("[data-lightbox]");
    this.lightboxImage = this.querySelector("[data-lightbox-image]");
    this.counter = this.querySelector("[data-lightbox-count]");
    this.index = 0;

    try {
      this.variantMedia = JSON.parse(this.querySelector("[data-variant-media]")?.textContent || "{}");
    } catch {
      this.variantMedia = {};
    }

    this.onClick = (event) => {
      const item = event.target.closest("[data-gallery-item]");
      if (!item || !this.contains(item)) return;
      // Mobile: tapping changes photo (left third goes back, elsewhere forward). Desktop: opens the lightbox.
      if (this.isMobile()) {
        const rect = item.getBoundingClientRect();
        const back = event.clientX - rect.left < rect.width / 3;
        this.slideTo(this.currentSlide() + (back ? -1 : 1));
        return;
      }
      this.open(this.items().indexOf(item));
    };
    this.addEventListener("click", this.onClick);

    this.counterEl = this.parentElement?.querySelector("[data-gallery-counter]");
    this.onScroll = () => {
      if (this.counterEl) this.counterEl.textContent = `(${this.currentSlide() + 1}/${this.slides().length})`;
    };
    this.addEventListener("scroll", this.onScroll, { passive: true });

    this.querySelector("[data-lightbox-close]")?.addEventListener("click", () => this.dialog.close());
    this.querySelector("[data-lightbox-prev]")?.addEventListener("click", () => this.show(this.index - 1));
    this.querySelector("[data-lightbox-next]")?.addEventListener("click", () => this.show(this.index + 1));
    // Clicking the dimmed page around the photo closes the lightbox.
    this.querySelector("[data-lightbox-stage]")?.addEventListener("click", (event) => {
      if (event.target === event.currentTarget) this.dialog.close();
    });
    this.dialog?.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft") this.show(this.index - 1);
      if (event.key === "ArrowRight") this.show(this.index + 1);
    });

    this.onVariantChange = (event) => {
      if (!event.target.closest("s-variant-picker")) return;
      const input = event.target;
      const variantId = input.dataset?.variantId || input.selectedOptions?.[0]?.dataset?.variantId;
      if (variantId) this.bringToTop(this.variantMedia[variantId]);
    };
    document.addEventListener("change", this.onVariantChange);
  }

  disconnectedCallback() {
    document.removeEventListener("change", this.onVariantChange);
  }

  isMobile() {
    return window.matchMedia("(max-width: 767px)").matches;
  }

  slides() {
    return [...this.children].filter((el) => el.classList.contains("shrink-0"));
  }

  currentSlide() {
    const width = this.clientWidth || 1;
    return Math.round(this.scrollLeft / width);
  }

  slideTo(index) {
    const count = this.slides().length;
    if (count === 0) return;
    const next = (index + count) % count;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.scrollTo({ left: next * this.clientWidth, behavior: reduce ? "auto" : "smooth" });
  }

  items() {
    return [...this.querySelectorAll("[data-gallery-item]")];
  }

  open(index) {
    if (!this.dialog || index < 0) return;
    this.show(index);
    this.dialog.showModal();
  }

  show(index) {
    const items = this.items();
    if (items.length === 0) return;
    this.index = (index + items.length) % items.length;
    const item = items[this.index];
    const fullSrc = item.dataset.fullSrc;
    const loadedSrc = item.querySelector("img")?.currentSrc;

    // Show the photo the page has already loaded instantly, then swap in the sharp one once it arrives.
    this.lightboxImage.removeAttribute("srcset");
    this.lightboxImage.src = loadedSrc || fullSrc;
    this.lightboxImage.alt = item.dataset.fullAlt || "";
    if (loadedSrc && fullSrc && loadedSrc !== fullSrc) {
      const sharp = new Image();
      const requested = this.index;
      sharp.onload = () => {
        if (this.index === requested) this.lightboxImage.src = fullSrc;
      };
      sharp.src = fullSrc;
    }
    if (this.counter) this.counter.textContent = `${this.index + 1} / ${items.length}`;
  }

  bringToTop(mediaId) {
    if (!mediaId) return;
    const item = this.querySelector(`[data-gallery-item][data-media-id="${mediaId}"]`);
    if (!item) return;
    const firstItem = this.querySelector("[data-gallery-item]");
    if (firstItem !== item) this.insertBefore(item, firstItem);
    const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
    // Desktop: the gallery is its own scroll, so rewind it. Mobile: scroll the page up to the gallery.
    if (this.scrollHeight > this.clientHeight + 1) {
      this.scrollTo({ top: 0, behavior });
      return;
    }
    this.scrollTo({ left: 0, behavior });
    const top = this.getBoundingClientRect().top + window.scrollY;
    if (window.scrollY > top) window.scrollTo({ top, behavior });
  }
}

if (!customElements.get("noia-product-gallery")) {
  customElements.define("noia-product-gallery", NoiaProductGallery);
}
